import React, { useState, useEffect } from 'react'
import { useGlobalsStore } from '@stores'
import { useToast } from '@hooks'
import { Table } from '@organisms'
import { cutGradeActions } from '@actions'
import { useHistory, NavLink } from 'react-router-dom'
import { fileUtils, textUtils } from '@utils'
import { exportCutGrades } from '../utils'

function CutGradeList() {
  const { showSuccessToast, showErrorToast } = useToast()
  const [cutGradeList, setCutGradeList] = useState([])
  const {
    orgsList: { all: orgsList }, getOrgsList,
    fixedIds, getFixedIds
  } = useGlobalsStore()
  const [columns, setColumns] = useState([])
  const history = useHistory()

  useEffect(() => {
    getCutGradeList('ACTIVE')
  }, [])

  useEffect(() => {
    getOrgsList()
    getFixedIds()
  }, [])

  useEffect(() => {
    setColumns([
      {
        Header: 'ID',
        accessor: 'id',
        dataType: 'string',
        Cell: cellInfo => {
          return (
            <NavLink
              className="link"
              to={`/buyer-settings/cutgrades/${cellInfo.row.original.id}`}
              id={cellInfo.row.original.id}
            >
              {cellInfo.value.slice(0, 4)}
            </NavLink>
          )
        }
      },
      {
        Header: 'Name',
        accessor: 'name',
        dataType: 'string'
      },
      {
        Header: 'Shape',
        accessor: (row) => row.Shape.name || ''
      },
      {
        Header: 'Owner',
        accessor: (row) => row.buyerId == null ? '* Preloaded *' : (orgsList?.find(org => org.orgId === row.buyerId)?.commonName || ''),
        dataType: 'string',
        id: 'owner'
      },
      {
        Header: 'Institute',
        accessor: 'institute',
        dataType: 'string'
      },
      {
        Header: 'Validation Status',
        accessor: (row) => textUtils.capitalize(row.sarineValidProportions, true),
        dataType: 'string'
      },
      {
        Header: 'Last Updated',
        accessor: 'updatedAt',
        dataType: 'date',
        filterType: 'date'
      }
    ])
  }, [cutGradeList, orgsList])

  function getCutGradeList(condition) {
    refreshCutGradeList({ condition, columns: '[Shape]' })
    .then(setCutGradeList)
    .catch(console.error)
  }
  async function refreshCutGradeList(params) {
    return await cutGradeActions.getCutGradeList(params).then(response => response.data.data)
  }

  function handleArchive(row) {
    return cutGradeActions.setCutGradeCondition(row.id, 'ARCHIVED')
    .then(() => {
      showSuccessToast('Cut Grade has been archived.')
    })
  }

  function handleUnarchive(row) {
    return cutGradeActions.setCutGradeCondition(row.id, 'ACTIVE')
    .then(() => {
      showSuccessToast('Cut Grade has been unarchived.')
    })
  }

  function handleRemove(row) {
    return cutGradeActions.setCutGradeCondition(row.id, 'DELETED')
    .then(() => {
      showSuccessToast('Cut Grade has been removed.')
    })
  }

  async function exportToExcel(rows) {
    try {
      const cutGrades = await refreshCutGradeList({ ids: `[${rows.map(row => row.id)}]`, columns: '[Shape,Proportions]' })
      return await exportCutGrades(cutGrades, { fixedIds, orgsList })
    } catch (err) {
      console.error(err)
      showErrorToast(err?.message || 'There was an error writing the excel.')
    }
  }

  function exportForAdvisor(cutGrades) {
    return cutGradeActions.getAllInstitutes(`[${cutGrades.map(grade => grade.id)}]`)
    .then(response => {
      const aiFile = response.data.data
      fileUtils.saveTextFile(aiFile, 'AllInstitutes.xml', { mimeType: 'text/xml' })
    })
  }

  const rowActions = [
    {
      actionName: 'archive',
      callback: handleArchive,
      shouldConfirm: true,
      shouldDisplay: (row) => row.buyerId && row.condition === 'ACTIVE'
    },
    {
      actionName: 'unarchive',
      callback: handleUnarchive,
      shouldConfirm: true,
      shouldDisplay: (row) => row.buyerId && row.condition === 'ARCHIVED'
    },
    {
      actionName: 'remove',
      callback: handleRemove,
      shouldConfirm: true,
      shouldDisplay: (row) => row.buyerId && row.condition !== 'DELETED'
    },
    {
      actionName: 'xlsxDownload',
      title: 'Download Excel',
      callback: async (row) => await exportToExcel([row]),
      shouldDisplay: (row) => row.condition !== 'DELETED',
      shouldRefresh: false,
      shouldConfirm: false
    }
  ]

  const tableTabs = [{
    label: 'Active',
    params: 'ACTIVE'
  },
  {
    label: 'Archived',
    params: 'ARCHIVED'
  }]
  const topBarActions = [
    {
      componentName: 'dropdown',
      enableOnSelect: true,
      options: [
        {
          label: 'Export for Advisor',
          callback: exportForAdvisor,
          value: 'exportXml'
        },
        {
          label: 'Download Excel',
          callback: exportToExcel,
          value: 'export'
        }
      ]
    },
    {
      label: 'Upload Sarine File',
      callback: () => history.push('/buyer-settings/cutgrades/upload')
    },
    {
      label: 'Create Cut Grade',
      callback: () => history.push('/buyer-settings/cutgrades/create')
    }
  ]

  const initialSort = [{
    id: 'updatedAt',
    desc: true
  },
  {
    id: 'owner',
    desc: true
  },
  {
    id: 'name',
    desc: false
  }
  ]

  return (
    <Table
      title='Cut Grades'
      data={cutGradeList}
      columns={columns}
      tableTabs={tableTabs}
      getDataCallback={getCutGradeList}
      topBarActions={topBarActions}
      rowActions={rowActions}
      initialSort={initialSort}
      isMultiSelect={true}
    />
  )
}

export default CutGradeList
